import React from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import Layout from 'components/Layout';
import SEO from 'components/SEO';
import Image from 'components/Image';
import Markdown from 'react-markdown';
import { Flex, Box, Container } from 'components/Grid';
import H1 from 'components/H1';
import H2 from 'components/H2';
import Link from 'components/Link';
import Text from 'components/Text';
import MediaGridItem from 'components/MediaGridItem';
import Video from 'components/Video';
import RichText from 'components/RichText';

const SingleCaseTemplateStyled = styled.div`
	.Case-Intro + .Case-Item {
		padding-top: 6rem;
	}
	.Case-Item--Text {
		p a {
			font-weight: bold;
		}
	}

	@media screen and (max-width: 500px) {
		.Case-Item h2:only-child {
			padding-bottom: 0;
		}

		.Case-Item--Text + .Case-Item--Text {
			> * {
				padding-top: 0;
			}
		}
	}
`;

const SingleCaseTemplate = props => {
	const data = props.data;

	const {
		headline,
		featuredImage,
		aboutText,
		darkText,
		rows,
		categories,
		title,
		seoDescription
	} = data.contentfulCase;
	let count = 0;

	const filteredGrid = rows
		? rows.reduce((acc, element) => {
				if (element.layout === '100 Full Width') {
					acc[count + 1] = {
						type: 'full',
						el: {
							...element.rowItems[0]
						}
					};
					count = count + 2;
				} else {
					acc[count] ? acc[count].push(element) : (acc[count] = [element]);
				}
				return acc;
		  }, [])
		: [];

	const gridWidths = {
		'100': '1fr',
		'50/50': '1fr 1fr',
		'60/40': '2fr 1fr',
		'40/60': '1fr 2fr'
	};

	const renderHero = () => {
		if (featuredImage.file.contentType === 'video/mp4') {
			return (
				<Video background={true} autoPlay muted loop playsInline src={featuredImage.file.url}>
					<video autoPlay muted loop playsInline src={featuredImage.file.url} />
				</Video>
			);
		} else {
			return (
				<Image
					style={{
						position: 'absolute'
					}}
					fadeIn={false}
					height={['100%']}
					fluid={featuredImage.fluid}
				/>
			);
		}
	};
	return (
		<SingleCaseTemplateStyled>
			<SEO title={title} image={featuredImage.file && featuredImage.file.url} description={seoDescription && seoDescription.internal.content} keywords={[`gatsby`, `application`, `react`]} />
			<Flex bg="client.accent" height={['360px', '600px']}>
				{featuredImage && renderHero()}
				<Container
					display="flex"
					alignItems="flex-end"
					justifyContent="space-between"
					color={!darkText ? 'white' : 'client.fontColor'}
					pb={[4]}
					height={['100%']}
				>
					<Flex width="100%" justifyContent="space-between">
						<Box mb={[3]}>
							<Link to="/">Sultan & Sultan</Link> / <Link to="/case">Case</Link> / {title}
						</Box>
						<Box>
							{categories &&
								categories
									.join('. / .')
									.split('.')
									.map(el => el)}
						</Box>
					</Flex>
					<Flex />
				</Container>
			</Flex>
			<Flex className="Case-Intro">
				<Container pb={filteredGrid.length ? [0, 0, 0] : [6, 5, 7]}>
					<Flex>
						<Box width={[1, 2 / 3]}>
							<H1 lineHeight="1.2">{headline}</H1>
							<Text>{aboutText && <Markdown source={aboutText.text} />}</Text>
						</Box>
					</Flex>
					<Flex />
				</Container>
			</Flex>
			{filteredGrid.map((item, i) =>
				item.type === 'full' ? (
					<Flex key={i} position="relative" className="Case-Item Case-Item--full">
						<MediaGridItem
							minHeight={['400px', '700px']}
							image={item.el.image}
							text={item.el.text}
							video={item.el.video}
							fullWidth={true}
							autoplayVideo={item.el.autoplayVideo || false}
						/>
					</Flex>
				) : (
					<Container key={i}>
						{item.map((row, i) => (
							<Flex
								gridGap={[2, 3]}
								mb={[2, 3]}
								key={i}
								grid
								gridTemplateColumns={['1fr', gridWidths[row.layout]]}
							>
								{row &&
									row.rowItems &&
									row.rowItems.map((rowItem, i) => (
										<Box
											height="100%"
											position="relative"
											key={i}
											className={`Case-Item Case-Item--${
												rowItem.type === 'ContentfulComponentTextBlock' ? 'Text' : 'Media'
											}`}
										>
											{rowItem.type === 'ContentfulComponentGridItem' && (
												<MediaGridItem
													p={[3]}
													height="100%"
													minHeight={[row.layout === '100' ? '100%' : '400px']}
													image={rowItem.image}
													text={rowItem.text}
													iframe={rowItem.iFrame}
													video={rowItem.video && rowItem.video}
													autoplayVideo={rowItem.autoplayVideo || false}
													fullWidth={row.layout === '100' ? true : false}
												/>
											)}
											{rowItem.type === 'ContentfulComponentTextBlock' && (
												<Box
													pt={[3, 5]}
													pr={i === 0 ? [0, 4] : ''}
													pl={i === 1 ? [0, 4] : ''}
													pb={!rowItem.richText ? [0, 5] : [3, 5]}
												>
													{rowItem.title && (
														<H2 lineHeight="1.3" mt={'-10px'} mb={[2, 4]}>
															{rowItem.title}
														</H2>
													)}
													{rowItem.richText && <RichText json={rowItem.richText.json} />}
												</Box>
											)}
										</Box>
									))}
							</Flex>
						))}
					</Container>
				)
			)}
		</SingleCaseTemplateStyled>
	);
};

export const pageQuery = graphql`
	query CaseBySlug($slug: String) {
		contentfulCase(slug: { eq: $slug }) {
			seoDescription {
				internal {
					content
				}
			}
			headline
			title
			darkText
			aboutText {
				text: aboutText
			}
			categories
			featuredImage {
				file {
					contentType
					url
				}
				fluid(quality: 100, maxWidth: 4000) {
					...GatsbyContentfulFluid
				}
			}
			rows: grid {
				layout
				rowItems {
					type: __typename
					... on ContentfulComponentGridItem {
						image {
							fluid(quality: 100, maxWidth: 2000) {
								...GatsbyContentfulFluid
							}
						}
						text
						autoplayVideo
						iFrame {
							iFrame
						}
						video {
							file {
								url
								fileName
								contentType
							}
						}
					}
					... on ContentfulComponentTextBlock {
						title
						richText: text {
							json
						}
					}
				}
			}
		}
	}
`;

export default SingleCaseTemplate;
